import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Compass, Heart, Eye, ArrowRight } from "lucide-react";

const PORTRAIT = "https://customer-assets.emergentagent.com/job_lead-store/artifacts/lak5i4uk_RobbieSouth.jpg";
const BACKDROP = "https://images.unsplash.com/photo-1505330622279-bf7d7fc918f4?w=1800";

const PILLARS = [
  { icon: Compass, label: "Direction", body: "South points leaders back to what matters most — character, purpose, service, impact." },
  { icon: Heart, label: "From the Inside Out", body: "Before you lead a team, a business, a family, or a community, you must first learn to lead yourself." },
  { icon: Eye, label: "People, Not Platforms", body: "We build leaders who serve, listen, and make decisions with integrity — leaders who build people, not just platforms." },
];

export default function About() {
  return (
    <Layout>
      <SEO
        title="About — The Story Behind South on Leadership"
        description="Robbie South on leading from the inside out — the character, purpose, and service that shape every leader worth following."
      />
      {/* HERO */}
      <section className="relative bg-brand-ink text-white overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 opacity-25">
          <img src={BACKDROP} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/85 to-brand-ink/65" />
        <div className="relative container-narrow py-24 md:py-36">
          <span className="overline text-brand-rust">Our Story</span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] mt-6 max-w-4xl">
            Leadership is not a title.
            <br />
            <em className="italic text-white/85">It's a daily choice.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
            South on Leadership exists to help people become the kind of leaders others can trust, follow, and grow from.
          </p>
        </div>
      </section>

      {/* BORN FROM A BELIEF */}
      <section className="border-b border-brand-line">
        <div className="container-narrow py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="overline text-brand-rust">Born from a belief</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-brand-ink leading-[1.1]">
              Leadership is a responsibility, a calling, and a daily choice.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-brand-ink text-lg leading-relaxed">
            <p>
              South on Leadership was born from a simple belief: leadership is not just a title — it is a responsibility, a calling, and a daily choice.
            </p>
            <p>
              The name <em className="italic">"South"</em> represents more than a last name. It represents direction, foundation, and grounded leadership. In a world where many people are chasing position, recognition, and authority, South on Leadership points leaders back to what matters most: character, purpose, service, and impact.
            </p>
          </div>
        </div>
      </section>

      {/* INSIDE OUT */}
      <section className="bg-white border-b border-brand-line">
        <div className="container-narrow py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="aspect-[4/5] overflow-hidden bg-brand-bg border border-brand-line">
              <img src={PORTRAIT} alt="Robbie South" className="w-full h-full object-cover" data-testid="about-portrait" />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="overline text-brand-rust">From the inside out</span>
            <p className="font-display text-3xl md:text-4xl mt-4 text-brand-ink leading-snug">
              Leadership is built from the inside out.
            </p>
            <p className="mt-6 text-brand-inkMuted leading-relaxed text-lg">
              Before someone can lead a team, a business, a family, or a community, they must first learn to lead themselves. That means developing discipline, emotional awareness, confidence, communication, and the courage to grow through discomfort.
            </p>
            <p className="mt-6 text-brand-inkMuted leading-relaxed text-lg">
              South on Leadership is for emerging leaders, established leaders, entrepreneurs, coaches, and everyday people who know they were created for more but need the tools, mindset, and guidance to step fully into their leadership.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-b border-brand-line">
        <div className="container-narrow py-20 md:py-28">
          <div className="max-w-3xl">
            <span className="overline text-brand-rust">What we stand for</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-brand-ink leading-[1.1]">
              Build leaders who lead with purpose — not for status.
            </h2>
            <p className="mt-6 text-brand-inkMuted leading-relaxed text-lg">
              Leaders who listen. Leaders who serve. Leaders who make decisions with integrity. Leaders who build people, not just platforms.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-px bg-brand-line">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.label} data-testid={`about-pillar-${i}`} className="bg-white p-8 md:p-10 flex flex-col">
                  <span className="overline text-brand-inkMuted">0{i + 1}</span>
                  <Icon className="w-6 h-6 mt-6 text-brand-rust" />
                  <p className="font-serif text-2xl mt-4 text-brand-ink leading-snug">{p.label}</p>
                  <p className="mt-3 text-brand-inkMuted leading-relaxed text-sm">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MISSION CLOSING */}
      <section className="bg-brand-ink text-white">
        <div className="container-narrow py-24 md:py-32 text-center max-w-4xl">
          <span className="overline text-brand-rust">Our mission</span>
          <p className="font-display text-3xl md:text-4xl mt-6 leading-snug text-white/95">
            To help people build themselves — so they can build others.
          </p>
          <div className="mt-12 max-w-2xl mx-auto space-y-6 text-white/75 leading-relaxed text-lg">
            <p>Because real leadership does not begin when you get the position.</p>
            <p className="font-display text-2xl md:text-3xl text-white/95 italic">
              It begins when you decide to become the person others can believe in.
            </p>
          </div>

          <div className="mt-14 flex flex-wrap gap-4 justify-center">
            <Link
              to="/courses"
              data-testid="about-cta-courses"
              className="inline-flex items-center gap-2 bg-white text-brand-ink hover:bg-brand-bg px-7 py-4 text-sm font-medium tracking-wide transition-colors"
            >
              Explore the courses <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/books"
              data-testid="about-cta-books"
              className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-brand-ink px-7 py-4 text-sm font-medium tracking-wide transition-colors"
            >
              See the books
            </Link>
          </div>
        </div>
      </section>

      {/* PODCAST — Listen on Spotify */}
      <section data-testid="about-podcast-section" className="bg-brand-bg border-t border-brand-line py-20 md:py-28">
        <div className="container-narrow grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="overline text-brand-rust">The Podcast</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-brand-ink leading-[1.05]">
              Hear it in <em className="italic">Robbie's own voice.</em>
            </h2>
            <p className="mt-6 text-brand-inkMuted leading-relaxed">
              Long-form conversations on character, decision-making, and the quiet standards leaders are held to. New episodes drop bi-weekly.
            </p>
            <a
              href="https://open.spotify.com/show/5BIKkX13rvdHq4falhhmRu"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-podcast-cta"
              className="inline-flex items-center gap-2 mt-8 bg-brand-ink text-white hover:bg-black px-7 py-3 text-sm font-medium tracking-wide transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 0a12 12 0 1 0 12 12A12 12 0 0 0 12 0zm5.5 17.32a.75.75 0 0 1-1.03.25c-2.82-1.72-6.37-2.11-10.55-1.16a.75.75 0 1 1-.33-1.46c4.57-1.04 8.5-.59 11.66 1.34a.75.75 0 0 1 .25 1.03zm1.47-3.27a.94.94 0 0 1-1.29.31c-3.23-1.99-8.16-2.56-11.99-1.4a.94.94 0 1 1-.55-1.8c4.38-1.33 9.81-.69 13.52 1.6a.94.94 0 0 1 .31 1.29zm.13-3.4c-3.87-2.3-10.27-2.51-13.96-1.39a1.13 1.13 0 1 1-.66-2.16c4.24-1.29 11.3-1.04 15.76 1.6a1.13 1.13 0 0 1-1.15 1.95z"/></svg>
              Listen on Spotify
            </a>
          </div>
          <div className="lg:col-span-7" data-testid="about-podcast-embed">
            <iframe
              title="South on Leadership Podcast on Spotify"
              src="https://open.spotify.com/embed/show/5BIKkX13rvdHq4falhhmRu?utm_source=generator"
              width="100%"
              height="352"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="border-0"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}
